/**
 * Shared preview-kind primitives for inline chat artifact previews.
 *
 * `pickPreviewKindForAttachment(name, content_type)` in
 *   `attachment-preview.ts` is content-type-first because the
 *   `attach_chat_artifact` server path already runs libmagic +
 *   `mimetypes.guess_type` and writes an authoritative MIME; the
 *   filename can be empty or extensionless (the agent-side sniff
 *   table is small and frequently falls back).
 */

export type PreviewKind =
  | 'image'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'spreadsheet'
  | 'docx'
  | 'markdown'
  | 'code'
  | 'html'
  | 'summary'
  | 'link'
  | 'fallback'

export const IMAGE_EXTS: ReadonlySet<string> = new Set([
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'avif',
  'bmp',
  'ico',
])

export const VIDEO_EXTS: ReadonlySet<string> = new Set([
  'mp4',
  'webm',
  'mov',
  'm4v',
  'ogv',
])

export const AUDIO_EXTS: ReadonlySet<string> = new Set([
  'mp3',
  'wav',
  'm4a',
  'ogg',
  'flac',
  'aac',
])

export const PDF_EXTS: ReadonlySet<string> = new Set(['pdf'])

export const SPREADSHEET_EXTS: ReadonlySet<string> = new Set([
  'xlsx',
  'xls',
  'csv',
  'tsv',
])

// Only the OOXML ``.docx`` format — mammoth cannot parse the legacy binary
// ``.doc`` (application/msword), so that stays on the fallback icon.
export const DOCX_EXTS: ReadonlySet<string> = new Set(['docx'])

export const MARKDOWN_EXTS: ReadonlySet<string> = new Set(['md', 'markdown', 'mdx'])

export const HTML_EXTS: ReadonlySet<string> = new Set(['html', 'htm'])

export const CODE_EXTS: ReadonlySet<string> = new Set([
  'json',
  'ts',
  'tsx',
  'js',
  'jsx',
  'py',
  'rb',
  'go',
  'rs',
  'java',
  'c',
  'cpp',
  'css',
  'scss',
  'yaml',
  'yml',
  'toml',
  'sh',
  'bash',
  'zsh',
  'sql',
  'xml',
  'txt',
])

export function extOf(path: string): string {
  const idx = path.lastIndexOf('.')
  if (idx < 0 || idx === path.length - 1) return ''
  return path.slice(idx + 1).toLowerCase()
}
